import type { Query, QueryClient } from "@tanstack/react-query";
import { resetAllStores } from "./stores.js";

export interface SwitchRosterArtistDeps {
  queryClient: QueryClient;
  /**
   * Leave the OLD artist's band-scoped SignalR group(s). The connection is person-bound and the
   * `org:{orgId}` group persists across an artist switch — only `band:{oldId}` leaves. Optional (a no-op before
   * realtime is wired / in a test without a client); the RosterArtistLayout ALSO leaves declaratively via
   * useLiveGroup, so this is the imperative, testable half of the same guarantee.
   */
  leaveBandGroup?: (bandId: string) => Promise<void> | void;
  /** Router navigation to the new artist's roster route. */
  navigate: (bandId: string) => void | Promise<void>;
}

/** True for a cache entry belonging to the given band tenant (`['band', bandId, …]`, the key convention).*/
function isBand(query: Query, bandId: string): boolean {
  const key = query.queryKey;
  return Array.isArray(key) && key[0] === "band" && key[1] === bandId;
}

/**
 * switchRosterArtist — the SCOPED close for a roster artist switch. Where the account-level
 * {@link switchBand} clears the WHOLE cache (a band ⇄ band switch changes the tenant AND the person surface,
 * as does logout), a roster artist switch stays inside the SAME org session — same person, same
 * `org:{orgId}` group, same org-lens — so it closes the departing artist without discarding it:
 *   (1) abort the old artist's in-flight requests (cancelQueries on `['band', oldId]` — TanStack's
 *       AbortController-backed cancellation, so nothing keeps downloading for a page nobody is on)
 *   (2) leave the old artist's `band:{oldId}` SignalR group   (3) mark the INCOMING artist's cache stale
 *   (4) reset every Zustand UI store   (5) navigate. (No push re-register: devices are per person, ART-259.)
 *
 * STEP 3 USED TO EVICT THE DEPARTING ARTIST (`removeQueries`), and dropping that is deliberate (PO,
 * 2026-09-02). Eviction made every return visit a cold load — a roster lens is a surface people click
 * ACROSS, so clicking back to an artist seen a minute ago rebuilt every card from spinners, and the roster
 * was the one place in the app opting out of the stale-while-revalidate the rest of it already runs on
 * (`createAppQueryClient`: staleTime 30s, refetch on focus + reconnect).
 *
 * Keeping it cannot reproduce the bleed the eviction was written against. That failure was the MVP roster
 * shell's repaint-only `selectArtist`, which mutated ONE variable and repainted chrome around modules that
 * were not keyed by band at all — so A's cards were literally still on screen under B's name. Here every
 * band-scoped read is keyed `['band', bandId, …]`, the very convention `isBand` below relies on, so B reads
 * B's namespace and a late A response lands in a namespace B never reads. The keying is the fence; the
 * eviction was a second one guarding a hole that had already been closed.
 *
 * So the incoming artist is INVALIDATED rather than trusted: its cached rows paint immediately and every
 * mounting component refetches over the top of them, which is what makes this a cache rather than a
 * snapshot. `refetchType: 'none'` because nothing is mounted yet at this instant — marking stale is the
 * whole job, and the page that mounts a tick later is what issues the fetch. The honest cost is that a
 * returning view can show the previous figures for the moment before that lands; TanStack's gcTime bounds
 * how long an unvisited artist is held at all.
 *
 * NB: tokens are NEVER cleared here (invariant 6 — only an explicit 401 clears tokens).
 */
export async function switchRosterArtist(
  oldBandId: string | undefined,
  newBandId: string,
  deps: SwitchRosterArtistDeps,
): Promise<void> {
  if (oldBandId && oldBandId !== newBandId) {
    // (1) abort in-flight for the OLD artist only (the org + person surface keeps loading). A cancelled
    // query reverts to its previous cached state, so this trims wasted work without emptying anything.
    await deps.queryClient.cancelQueries({ predicate: (q) => isBand(q, oldBandId) });

    // (2) leave the old artist's band group (the person + org groups persist).
    if (deps.leaveBandGroup) await deps.leaveBandGroup(oldBandId);
  }

  // (3) the departing artist is KEPT (see the note above); the ARRIVING one is marked stale so its cached
  // cards paint at once and are refetched over, rather than being served as a snapshot of whenever it was
  // last open.
  await deps.queryClient.invalidateQueries({
    predicate: (q) => isBand(q, newBandId),
    refetchType: "none",
  });

  // (4) reset every Zustand UI store (artist-scoped UI state must not carry across the switch).
  resetAllStores();

  // (5) navigate to the new artist's roster route (the layout refetches its org-lens navigation).
  await deps.navigate(newBandId);
}
